// @ts-check

import BaseModel from "@kaspernj/api-maker/build/base-model.js"
import Option from "./option.js"

const modelClassData = {
  "attributes": {
    "id": {
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": "uuid"
      },
      "name": "id",
      "selected_by_default": null,
      "translated": null
    },
    "option_id": {
      "column": {
        "default": null,
        "name": "option_id",
        "null": false,
        "type": "uuid"
      },
      "name": "option_id",
      "selected_by_default": null,
      "translated": null
    },
    "x": {
      "column": {
        "default": null,
        "name": "x",
        "null": true,
        "type": "integer"
      },
      "name": "x",
      "selected_by_default": null,
      "translated": null
    },
    "y": {
      "column": {
        "default": null,
        "name": "y",
        "null": true,
        "type": "integer"
      },
      "name": "y",
      "selected_by_default": null,
      "translated": null
    }
  },
  "className": "OptionPosition",
  "collectionKey": "option_positions",
  "collectionName": "option_positions",
  "i18nKey": "option_position",
  "camelizedLower": "optionPosition",
  "name": "OptionPosition",
  "nameDasherized": "option-position",
  "pluralName": "option_positions",
  "ransackable_associations": [
    {
      "className": "Element",
      "collectionName": "elements",
      "foreignKey": "element_id",
      "name": "element",
      "macro": "has_one",
      "resource_name": "Element",
      "through": "option"
    },
    {
      "className": "Log",
      "collectionName": "logs",
      "foreignKey": "trackable_id",
      "name": "logs",
      "macro": "has_many",
      "resource_name": "Log",
      "through": null
    },
    {
      "className": "Option",
      "collectionName": "options",
      "foreignKey": "option_id",
      "name": "option",
      "macro": "belongs_to",
      "resource_name": "Option",
      "through": null
    },
    {
      "className": "Tagging",
      "collectionName": "taggings",
      "foreignKey": "resource_id",
      "name": "taggings",
      "macro": "has_many",
      "resource_name": "Tagging",
      "through": null
    },
    {
      "className": "Tag",
      "collectionName": "tags",
      "foreignKey": "tag_id",
      "name": "tags",
      "macro": "has_many",
      "resource_name": "Tag",
      "through": "taggings"
    },
    {
      "className": "Tenant",
      "collectionName": "tenants",
      "foreignKey": "tenant_id",
      "name": "tenant",
      "macro": "belongs_to",
      "resource_name": "Tenant",
      "through": null
    },
    {
      "className": "WorkerPlugins::WorkplaceLink",
      "collectionName": "workplace_links",
      "foreignKey": "resource_id",
      "name": "workplace_links",
      "macro": "has_many",
      "resource_name": "WorkplaceLink",
      "through": null
    }
  ],
  "ransackable_attributes": [
    {
      "name": "created_at",
      "column": {
        "default": null,
        "name": "created_at",
        "null": false,
        "type": "datetime"
      }
    },
    {
      "name": "id",
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": "uuid"
      }
    },
    {
      "name": "id_string",
      "column": null
    },
    {
      "name": "option_id",
      "column": {
        "default": null,
        "name": "option_id",
        "null": false,
        "type": "uuid"
      }
    },
    {
      "name": "tenant_id",
      "column": {
        "default": null,
        "name": "tenant_id",
        "null": false,
        "type": "uuid"
      }
    },
    {
      "name": "updated_at",
      "column": {
        "default": null,
        "name": "updated_at",
        "null": false,
        "type": "datetime"
      }
    },
    {
      "name": "x",
      "column": {
        "default": null,
        "name": "x",
        "null": true,
        "type": "integer"
      }
    },
    {
      "name": "y",
      "column": {
        "default": null,
        "name": "y",
        "null": true,
        "type": "integer"
      }
    }
  ],
  "ransackable_scopes": [
    {
      "name": "in_current_workplace"
    },
    {
      "name": "soft_deleted_filter"
    },
    {
      "name": "sort_by_in_current_workplace_asc"
    },
    {
      "name": "sort_by_in_current_workplace_desc"
    }
  ],
  "relationships": [
    {
      "className": "Option",
      "collectionName": "options",
      "foreignKey": "option_id",
      "name": "option",
      "macro": "belongs_to",
      "resource_name": "Option",
      "through": null
    }
  ],
  "paramKey": "option_position",
  "primaryKey": "id"
}

/** Frontend model for OptionPosition. */
export default class OptionPosition extends BaseModel {
  /** @returns {typeof modelClassData} */
  static modelClassData() {
    return modelClassData
  }

  /** @returns {string} */
  id() {
    return this.readAttributeUnderscore("id")
  }

  /** @returns {boolean} */
  hasId() {
    const value = this.id()

    return this._isPresent(value)
  }

  /** @returns {string} */
  optionId() {
    return this.readAttributeUnderscore("option_id")
  }

  /** @returns {boolean} */
  hasOptionId() {
    const value = this.optionId()

    return this._isPresent(value)
  }

  /** @returns {number | null} */
  x() {
    return this.readAttributeUnderscore("x")
  }

  /** @returns {boolean} */
  hasX() {
    const value = this.x()

    return this._isPresent(value)
  }

  /** @returns {number | null} */
  y() {
    return this.readAttributeUnderscore("y")
  }

  /** @returns {boolean} */
  hasY() {
    const value = this.y()

    return this._isPresent(value)
  }

  /** @returns {import("./option.js").default | null} */
  option() {
    return this._readBelongsToReflection({modelClass: Option, reflectionName: "option"})
  }

  /** @returns {Promise<import("./option.js").default | null>} */
  loadOption() {
    if (!("optionId" in this)) throw new Error("Foreign key method wasn't defined: optionId")

    const id = this.optionId()
    const ransack = {}

    ransack["id_eq"] = id

    return this._loadBelongsToReflection(
      {
        reflectionName: "option",
        model: this,
        modelClass: Option
      },
      {ransack}
    )
  }

  /** @returns {Promise<import("./option.js").default | null>} */
  ensureOptionLoaded() {
    return this.ensureAssociationLoaded("option")
  }
}
